import './authGate.js'; // private-tool access gate (awning tool is NOT gated)
import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { SSAOPass } from 'three/addons/postprocessing/SSAOPass.js';
import { RGBELoader } from 'three/addons/loaders/RGBELoader.js';
import Stats from 'stats.js';
import GUI from 'lil-gui';
import { PergolaBuilder } from './PergolaBuilder.js';
import { PricingCalculator } from './PricingCalculator.js';
import { BEAM_TYPES } from './BeamManager.js';
import { LIGHT_TYPES, LIGHTS_FAN_PACKAGE } from './LightingManager.js';
import { WeatherSimulation, WIND_LOAD_INFO, SNOW_LOAD_INFO } from './WeatherSimulation.js';
import { ROOF_TYPES, LAYOUT_MODES } from './ZoneManager.js';
import { IN_TO_M } from './CadSpecs.js';
import { PhotoMode } from './PhotoMode.js';
import { SunSimulator } from './SunSimulator.js';

class App {
  constructor() {
    this.clock = new THREE.Clock();
    this.frameCount = 0;
    this.lastFpsTime = 0;
    this._boundListeners = []; // Track event listeners for cleanup

    // Show loading state
    this._showLoading();

    try {
      this.initRenderer();
    } catch (e) {
      this._showError('WebGL is not supported or failed to initialize. Please use a modern browser with GPU acceleration enabled.');
      console.error('WebGL init failed:', e);
      return;
    }

    this.initScene();
    this.initEnvironmentMap();
    this.initPostProcessing();
    this.initControls();
    this.isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (this.isDev) this.initStats();
    this.pergola = new PergolaBuilder(this.scene);
    // Alias PergolaBuilder's shadeManager so App can mutate it directly
    this.shadeManager = this.pergola.shadeManager;
    // Apply environment map to pergola materials for metallic reflections
    this._applyEnvMap();
    this.pricing = new PricingCalculator();
    this.weather = new WeatherSimulation(this.scene);
    this.weather.setRoofCloseCallback(() => this.pergola.closeRoof());
    this.isNightMode = false;
    this.bloomEnabled = false; // bloom only in night mode for full daytime quality
    this.pergolaOrientation = 270; // degrees; 270 = front face West (wide side facing west, default)
    this.facingFlipped = false;    // true when length side faces the chosen direction
    this.splitMode = false;

    // Sun simulation
    this.sunSim = new SunSimulator();
    this._defaultLightPos       = { x: 10, y: 15, z: 8 };
    this._defaultLightColor     = 0xfff4e0;
    this._defaultLightIntensity = 1.0;

    // Photo Mode — path-traced rendering
    this.photoMode = new PhotoMode(this.renderer, this.scene, this.camera);
    this.photoMode.onSampleUpdate((samples, max) => {
      const samplesEl = document.getElementById('photo-samples');
      const progressEl = document.getElementById('photo-progress');
      if (samplesEl) samplesEl.textContent = typeof samples === 'string' ? samples : `Samples: ${samples} / ${max}`;
      if (progressEl && typeof samples === 'number') progressEl.style.width = `${(samples / max * 100).toFixed(1)}%`;
    });
    this.photoMode.onDisable(() => {
      // Restore normal rendering when photo mode is turned off
      const btn = document.getElementById('btn-photo-mode');
      if (btn) { btn.textContent = 'Enable Photo Mode'; btn.style.background = ''; }
      const statusEl = document.getElementById('photo-mode-status');
      if (statusEl) statusEl.style.display = 'none';
    });

    // Apply default floor material (grass)
    if (this.floorMesh) {
      this.floorMesh.material = this.pergola.getFloorMaterial();
    }

    try {
      this.bindUI();
      if (this.isDev) this.initDebugGUI();
      this.readURLParams(); // auto-configure from query string (EasyPrice integration)
      this.renderZoneList();
      this.renderUserPostList();
      this.renderShadeList();
      this.updatePricingSummary();

      // Apply default west-facing orientation (270°) after pergola is fully built
      this.setPergolaOrientation(this.pergolaOrientation);

      // Sync blade angle slider to match PergolaBuilder's internal default (45°)
      const baSlider = document.getElementById('blade-angle');
      const baVal    = document.getElementById('blade-angle-val');
      if (baSlider) baSlider.value = this.pergola.bladeAngle;
      if (baVal)    baVal.textContent = `${this.pergola.bladeAngle}°`;
    } catch (e) {
      console.error('Init error:', e);
    }

    // Hide loading, start render — always runs even if init partially failed
    this._hideLoading();
    this.animate();

    this._addListener(window, 'resize', () => this.onResize());

    // Mobile toolbar drag-to-expand
    const toolbar = document.getElementById('toolbar');
    this._addListener(toolbar, 'click', (e) => {
      if (window.innerWidth <= 640 && e.target === toolbar) {
        toolbar.classList.toggle('expanded');
      }
    });

    // Two-view navigation: Form ↔ Render
    const formView   = document.getElementById('form-view');
    const renderView = document.getElementById('render-view');
    const btnPreview = document.getElementById('btn-preview-3d');
    const btnConfig  = document.getElementById('btn-configure');
    const statusBar = document.getElementById('status-bar');
    if (statusBar) statusBar.style.display = 'none';
    if (btnPreview && btnConfig && formView && renderView) {
      this._addListener(btnPreview, 'click', () => this.showRenderView());
      this._addListener(btnConfig,  'click', () => this.showFormView());
    }
  }

  // ─── Event listener tracking for cleanup ──
  _addListener(target, event, handler, options) {
    target.addEventListener(event, handler, options);
    this._boundListeners.push({ target, event, handler, options });
  }

  dispose() {
    // Clean up all tracked event listeners
    for (const { target, event, handler, options } of this._boundListeners) {
      target.removeEventListener(event, handler, options);
    }
    this._boundListeners = [];
    if (this.stats) this.stats.dom.remove();
    if (this.gui) this.gui.destroy();
    this.weather.stopWeather();
    this.renderer.dispose();
  }

  // ─── Loading / Error UI ──
  _showLoading() {
    const el = document.createElement('div');
    el.id = 'loading-overlay';
    el.innerHTML = '<div class="loading-spinner"></div><div>Loading 3D Configurator...</div>';
    document.body.appendChild(el);
  }

  _hideLoading() {
    const el = document.getElementById('loading-overlay');
    if (el) el.remove();
  }

  _showError(msg) {
    this._hideLoading();
    const el = document.createElement('div');
    el.id = 'error-overlay';
    el.innerHTML = `<div style="max-width:400px;text-align:center;padding:40px">
      <h2 style="color:#f07c7c;margin-bottom:16px">Unable to Start</h2>
      <p style="color:#aaa;line-height:1.6">${msg}</p>
    </div>`;
    document.body.appendChild(el);
  }

  initRenderer() {
    this.canvas = document.getElementById('renderer-canvas');
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      alpha: false,
      preserveDrawingBuffer: true, // required for path tracer accumulation
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 0.9;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
  }

  initScene() {
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xd4e6f1);
    this.scene.fog = new THREE.Fog(0xd4e6f1, 50, 120);

    this.camera = new THREE.PerspectiveCamera(
      50,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    // Position camera to frame a 12'×16' pergola nicely
    this.camera.position.set(7, 4, 9);

    // ── Lighting (conservative defaults — HDRI takes over once loaded) ──
    this.ambientLight = new THREE.AmbientLight(0xffffff, 0.3);
    this.scene.add(this.ambientLight);

    // Sun
    this.directionalLight = new THREE.DirectionalLight(0xfff4e0, 1.0);
    this.directionalLight.position.set(10, 15, 8);
    this.directionalLight.castShadow = true;
    this.directionalLight.shadow.mapSize.width = 4096;
    this.directionalLight.shadow.mapSize.height = 4096;
    this.directionalLight.shadow.camera.near = 0.5;
    this.directionalLight.shadow.camera.far = 60;
    this.directionalLight.shadow.camera.left = -20;
    this.directionalLight.shadow.camera.right = 20;
    this.directionalLight.shadow.camera.top = 20;
    this.directionalLight.shadow.camera.bottom = -20;
    this.directionalLight.shadow.bias = -0.0005;
    this.directionalLight.shadow.normalBias = 0.02;
    this.scene.add(this.directionalLight);

    // Hemisphere fill — subtle, HDRI handles most sky/ground lighting
    this.hemiLight = new THREE.HemisphereLight(0xc0e0ff, 0xf0f0f0, 0.2);
    this.scene.add(this.hemiLight);

    // ── Ground ──
    this.gridHelper = new THREE.GridHelper(30, 30, 0xcccccc, 0xdddddd);
    this.gridHelper.visible = false;
    this.scene.add(this.gridHelper);

    this.axesHelper = new THREE.AxesHelper(2);
    this.axesHelper.visible = false;
    this.scene.add(this.axesHelper);

    // Shadow-receiving ground
    const groundGeo = new THREE.PlaneGeometry(60, 60);
    const groundMat = new THREE.ShadowMaterial({ opacity: 0.2 });
    this.ground = new THREE.Mesh(groundGeo, groundMat);
    this.ground.rotation.x = -Math.PI / 2;
    this.ground.position.y = -0.01;
    this.ground.receiveShadow = true;
    this.scene.add(this.ground);

    // Floor surface (will be swapped by floor type control)
    this.floorMesh = new THREE.Mesh(new THREE.PlaneGeometry(60, 60),
      new THREE.MeshStandardMaterial({ color: 0xf0ebe3, roughness: 0.8, metalness: 0.0 }));
    this.floorMesh.rotation.x = -Math.PI / 2;
    this.floorMesh.position.y = -0.02;
    this.floorMesh.receiveShadow = true;
    this.scene.add(this.floorMesh);
  }

  initControls() {
    this.orbitControls = new OrbitControls(this.camera, this.canvas);
    this.orbitControls.enableDamping = true;
    this.orbitControls.dampingFactor = 0.08;
    this.orbitControls.maxPolarAngle = Math.PI * 0.85;
    this.orbitControls.target.set(0, 1.2, 0);

    // Reset path tracer accumulation when camera moves
    this.orbitControls.addEventListener('change', () => {
      if (this.photoMode && this.photoMode.enabled) {
        this.photoMode.cameraChanged();
      }
    });
  }

  // ─── Environment map (HDRI or procedural fallback) ──
  initEnvironmentMap() {
    // Start with procedural fallback immediately (no async wait)
    this._createProceduralEnvMap();

    // Then load real HDRI asynchronously
    this._loadHDRI();
  }

  _createProceduralEnvMap() {
    const size = 256;
    const data = new Float32Array(size * size * 4);
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const i = (y * size + x) * 4;
        const ny = y / size;
        data[i] = 0.6 + ny * 0.4;
        data[i + 1] = 0.7 + ny * 0.3;
        data[i + 2] = 0.85 + ny * 0.15;
        data[i + 3] = 1.0;
      }
    }
    const texture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat, THREE.FloatType);
    texture.mapping = THREE.EquirectangularReflectionMapping;
    texture.needsUpdate = true;
    this.proceduralEnvMap = texture;
    if (!this.hdriEnvMap) {
      this.envMap = texture;
      this.scene.environment = texture;
    }
  }

  _loadHDRI() {
    const loader = new RGBELoader();
    const basePath = import.meta.env.BASE_URL || '/';
    loader.load(
      `${basePath}hdri/kloppenheim_06_puresky_1k.hdr`,
      (texture) => {
        texture.mapping = THREE.EquirectangularReflectionMapping;
        this.hdriEnvMap = texture;
        this.hdriIntensity = 1.0;

        // Apply HDRI as environment lighting
        this.envMap = texture;
        this.scene.environment = texture;
        this.scene.environmentIntensity = 1.2;
        this._applyEnvMap();
        this.setHDRIBackground(true);

        // HDRI carries the lighting — pull back manual lights so they don't wash it out
        if (!this.isNightMode && !(this.sunSim && this.sunSim.enabled)) {
          this.directionalLight.intensity = 0.5;
          this.ambientLight.intensity = 0.1;
          this.hemiLight.intensity = 0.0;
          this.renderer.toneMappingExposure = 0.85;
        }
      },
      undefined,
      (err) => {
        console.warn('HDRI load failed, using procedural fallback:', err);
      }
    );
  }

  setEnvMapType(type) {
    if (type === 'hdri' && this.hdriEnvMap) {
      this.envMap = this.hdriEnvMap;
      this.scene.environment = this.hdriEnvMap;
      this.scene.environmentIntensity = this.hdriIntensity || 1.0;
    } else {
      this.envMap = this.proceduralEnvMap;
      this.scene.environment = this.proceduralEnvMap;
      this.scene.environmentIntensity = 1.0;
    }
    this._applyEnvMap();
  }

  setHDRIBackground(show) {
    if (show && this.hdriEnvMap) {
      this.scene.background = this.hdriEnvMap;
      this.scene.fog = null;
    } else {
      const bgColor = this.isNightMode ? new THREE.Color(0x0a0a1a) : new THREE.Color(0xd4e6f1);
      this.scene.background = bgColor;
      this.scene.fog = new THREE.Fog(bgColor, 50, 120);
    }
  }

  setHDRIIntensity(value) {
    this.hdriIntensity = value;
    if (this.scene.environment === this.hdriEnvMap) {
      this.scene.environmentIntensity = value;
    }
  }

  _applyEnvMap() {
    if (!this.envMap || !this.pergola) return;
    const mats = this.pergola.materials;
    // Apply environment map to metallic materials for reflections
    for (const [key, mat] of Object.entries(mats)) {
      if (mat && mat.isMeshStandardMaterial && mat.metalness > 0.05) {
        mat.envMap = this.envMap;
        mat.envMapIntensity = 0.4;
        mat.needsUpdate = true;
      }
    }
  }

  // ─── Post-processing (SSAO + bloom for LED glow) ──
  initPostProcessing() {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const pixelRatio = this.renderer.getPixelRatio();

    // Render target matching renderer resolution
    const renderTarget = new THREE.WebGLRenderTarget(w * pixelRatio, h * pixelRatio, {
      type: THREE.HalfFloatType,
      depthBuffer: true,
      depthTexture: new THREE.DepthTexture(w * pixelRatio, h * pixelRatio),
    });

    this.composer = new EffectComposer(this.renderer, renderTarget);
    this.composer.addPass(new RenderPass(this.scene, this.camera));

    // SSAO — subtle ambient occlusion for depth and realism
    this.ssaoPass = new SSAOPass(this.scene, this.camera, w, h);
    this.ssaoPass.kernelRadius = 0.3;
    this.ssaoPass.minDistance = 0.001;
    this.ssaoPass.maxDistance = 0.15;
    this.ssaoPass.output = SSAOPass.OUTPUT.Default;
    this.composer.addPass(this.ssaoPass);

    this.bloomPass = new UnrealBloomPass(
      new THREE.Vector2(w * pixelRatio, h * pixelRatio),
      0.8,   // strength — only used in night mode
      0.3,   // radius
      0.6    // threshold — only bright emissives bloom
    );
    this.bloomPass.enabled = false; // off in daytime, enabled in night mode
    this.composer.addPass(this.bloomPass);
    this.composer.addPass(new OutputPass());

    // SSAO off by default — adds gray cast and reduces FPS; enable in debug GUI if needed
    this.ssaoEnabled = false;
  }

  // ─── Stats.js (FPS, MS, MB panels) ──
  initStats() {
    this.stats = new Stats();
    this.stats.showPanel(0); // FPS
    this.stats.dom.style.cssText = 'position:fixed;top:0;left:0;z-index:20;opacity:0.8;';
    document.body.appendChild(this.stats.dom);
  }

  // ─── lil-gui debug panel ──
  initDebugGUI() {
    this.gui = new GUI({ title: 'Debug', width: 260 });
    this.gui.domElement.style.position = 'fixed';
    this.gui.domElement.style.top = '0';
    this.gui.domElement.style.left = '80px';
    this.gui.domElement.style.zIndex = '20';
    this.gui.close(); // collapsed by default

    // Bloom controls
    const bloom = this.gui.addFolder('Bloom');
    bloom.add(this.bloomPass, 'strength', 0, 2, 0.01).name('Strength');
    bloom.add(this.bloomPass, 'radius', 0, 1, 0.01).name('Radius');
    bloom.add(this.bloomPass, 'threshold', 0, 1, 0.01).name('Threshold');

    // Renderer
    const render = this.gui.addFolder('Renderer');
    render.add(this.renderer, 'toneMappingExposure', 0.1, 3, 0.05).name('Exposure');
    render.add({ pixelRatio: this.renderer.getPixelRatio() }, 'pixelRatio', 1, 3, 0.5)
      .name('Pixel Ratio')
      .onChange(v => this.renderer.setPixelRatio(v));

    // Camera
    const cam = this.gui.addFolder('Camera');
    cam.add(this.camera, 'fov', 20, 100, 1).name('FOV')
      .onChange(() => this.camera.updateProjectionMatrix());

    // Scene
    const scene = this.gui.addFolder('Scene');
    scene.add(this.scene.fog, 'near', 1, 100, 1).name('Fog Near');
    scene.add(this.scene.fog, 'far', 10, 500, 1).name('Fog Far');
  }

  // ─── Helper: inches → "X'-Y\"" display ───
  inchesToDisplay(totalInches) {
    let ft = Math.floor(totalInches / 12);
    let inches = Math.round(totalInches % 12);
    if (inches === 12) { ft += 1; inches = 0; }
    return `${ft}'-${inches}"`;
  }

  // ─── UI Bindings ──────────────────────────────────────────────
  bindUI() {
    const pergola = this.pergola;

    // Dimension sliders are now rendered inside each zone entry by renderZoneList()

    // ── Wall attachment toggles ──
    const wallSides = ['front', 'back', 'left', 'right'];
    for (const side of wallSides) {
      document.getElementById(`wall-${side}`).addEventListener('change', (e) => {
        // Enforce max 3 walls in UI
        const checkedCount = wallSides.filter(s =>
          document.getElementById(`wall-${s}`).checked
        ).length;
        if (checkedCount > 3) {
          e.target.checked = false;
          return;
        }
        // Disable shades on newly-walled side BEFORE rebuild
        const newWalls = { ...pergola.config.walls, [side]: e.target.checked };
        this.shadeManager.onWallChanged(newWalls);
        pergola.setWall(side, e.target.checked);
        this.renderShadeList();
        this.updatePricingSummary();
        // Disable remaining unchecked if 3 are checked
        this.enforceMaxWalls();
      });
    }

    // ── Shade mount type ──
    for (const type of ['inside', 'outside']) {
      document.getElementById(`shade-mount-${type}`)?.addEventListener('change', () => {
        this.shadeManager.setMountType(type);
        pergola.build();
        pergola.setBladeAngle(pergola.bladeAngle);
        this.updatePricingSummary();
      });
    }

    // ── Shade side buttons (static HTML — always visible) ──
    for (const side of ['front', 'back', 'left', 'right']) {
      document.getElementById(`shade-btn-${side}`)?.addEventListener('click', () => {
        if (this.pergola.config.walls[side]) return;
        const nowEnabled = !this.shadeManager.shades[side].enabled;
        this.shadeManager.setShadeEnabled(side, nowEnabled);
        this.renderShadeList();
        this.pergola.build();
        this.pergola.setBladeAngle(this.pergola.bladeAngle);
        this.updatePricingSummary();
        this._updateShadeOpenRow();
      });
      document.getElementById(`shade-pos-${side}`)?.addEventListener('input', (e) => {
        const pct = parseInt(e.target.value, 10);
        const valEl = document.getElementById(`shade-pos-${side}-val`);
        if (valEl) valEl.textContent = pct + '%';
        this.shadeManager.setShadePosition(side, pct / 100);
        this.pergola.build();
        this.pergola.setBladeAngle(this.pergola.bladeAngle);
      });
    }

    // ── Post management: Cantilever sliders ──
    for (let i = 0; i < 4; i++) {
      const slider = document.getElementById(`cant-${i}`);
      const val = document.getElementById(`cant-${i}-val`);
      slider.addEventListener('input', () => {
        const v = parseInt(slider.value);
        val.textContent = v > 0 ? `${v}"` : '0"';
        pergola.setCantilever(i, v);
        this.updatePricingSummary();
        this.updatePostWarnings();
      });
    }

    // ── Post management: Add center post ──
    // Posts are placed at the midpoint of each fascia/beam edge
    document.getElementById('add-post-center').addEventListener('click', () => {
      const info = pergola.getSpecInfo();
      pergola.addUserPost(0, -info.lengthInches / 2, 'Mid Front');
      this.renderUserPostList();
      this.updatePricingSummary();
    });
    document.getElementById('add-post-back').addEventListener('click', () => {
      const info = pergola.getSpecInfo();
      pergola.addUserPost(0, info.lengthInches / 2, 'Mid Back');
      this.renderUserPostList();
      this.updatePricingSummary();
    });
    document.getElementById('add-post-left').addEventListener('click', () => {
      const info = pergola.getSpecInfo();
      pergola.addUserPost(-info.widthInches / 2, 0, 'Mid Left');
      this.renderUserPostList();
      this.updatePricingSummary();
    });
    document.getElementById('add-post-right').addEventListener('click', () => {
      const info = pergola.getSpecInfo();
      pergola.addUserPost(info.widthInches / 2, 0, 'Mid Right');
      this.renderUserPostList();
      this.updatePricingSummary();
    });

    // ── Center post toggles (paired edges) ──
    document.getElementById('center-post-width').addEventListener('change', (e) => {
      pergola.setCenterPostToggle('widthSide', e.target.checked);
      this.renderUserPostList();
      this.updatePricingSummary();
      this.updatePostWarnings();
    });
    document.getElementById('center-post-length').addEventListener('change', (e) => {
      pergola.setCenterPostToggle('lengthSide', e.target.checked);
      this.renderUserPostList();
      this.updatePricingSummary();
      this.updatePostWarnings();
    });

    // ── Color controls (legacy toolbar IDs — removed in redesign; no-ops) ──
    document.getElementById('color-post')?.addEventListener('input', (e) => {
      pergola.setColor('post', e.target.value);
    });
    document.getElementById('color-beam')?.addEventListener('input', (e) => {
      pergola.setColor('beam', e.target.value);
    });
    document.getElementById('color-blade')?.addEventListener('input', (e) => {
      pergola.setColor('blade', e.target.value);
    });
    document.getElementById('color-all')?.addEventListener('input', (e) => {
      pergola.setAllColor(e.target.value);
      document.getElementById('color-post').value = e.target.value;
      document.getElementById('color-beam').value = e.target.value;
      document.getElementById('color-blade').value = e.target.value;
    });

    // ── Azenco color presets (official powder coat colors) ──
    // Hex values are calibrated for metalness=0.85 rendering — lighter than real RAL
    // so they read correctly as satin metal rather than appearing too dark.
    const presets = {
      'preset-white':    { post: '#f4f0ec', beam: '#f4f0ec', blade: '#f4f0ec', label: 'RAL 9003 Signal White' },
      'preset-charcoal': { post: '#78726a', beam: '#78726a', blade: '#78726a', label: 'Gris 2900 Sable' },
      'preset-black':    { post: '#1e1c1a', beam: '#1e1c1a', blade: '#1e1c1a', label: 'Equinox Black' },
      'preset-bronze':   { post: '#4e3c2c', beam: '#4e3c2c', blade: '#4e3c2c', label: 'RAL 8019 Bronze' },
    };

    for (const [id, colors] of Object.entries(presets)) {
      document.getElementById(id)?.addEventListener('click', () => {
        pergola.setBladeWoodGrain(false); // clear wood grain if active
        pergola.setColor('post', colors.post);
        pergola.setColor('beam', colors.beam);
        pergola.setColor('blade', colors.blade);
        const cpEl = document.getElementById('color-post'); if (cpEl) cpEl.value = colors.post;
        const cbEl = document.getElementById('color-beam'); if (cbEl) cbEl.value = colors.beam;
        const clEl = document.getElementById('color-blade'); if (clEl) clEl.value = colors.blade;
      });
    }

    // Wood Grain — texture on louvers, frame stays current color
    document.getElementById('preset-woodgrain')?.addEventListener('click', () => {
      pergola.setBladeWoodGrain(true);
    });

    // ── Zone controls ──
    document.getElementById('btn-add-zone')?.addEventListener('click', () => {
      this.pergola.setZoneAxis('length');
      const result = this.pergola.addZone('r-blade');
      if (result && result.error) {
        this.showZoneValidationError(result.error);
        return;
      }
      this.renderZoneList();
      this.updatePricingSummary();
    });
    document.getElementById('add-zone-width')?.addEventListener('click', () => {
      this.pergola.setZoneAxis('width');
      const result = this.pergola.addZone('r-blade');
      if (result && result.error) {
        this.showZoneValidationError(result.error);
        return;
      }
      this.renderZoneList();
      this.updatePricingSummary();
    });

    // ── Beam controls ──
    const beamTypes = ['accessory', 'structural', 'heater', 'reinforcement'];
    for (const type of beamTypes) {
      document.getElementById(`add-beam-${type}`).addEventListener('click', () => {
        // Default to width axis, centered
        const beam = pergola.addBeam(type, 'width', 0);
        this.renderBeamList();
        this.updatePricingSummary();
      });
    }

    // ── Pergola Lighting controls ──
    const recessedSlider = document.getElementById('light-recessed');
    const recessedVal = document.getElementById('light-recessed-val');
    recessedSlider.addEventListener('input', () => {
      const v = parseInt(recessedSlider.value);
      recessedVal.textContent = v;
      pergola.setLighting('recessed', v);
      this.showLightingWarnings();
      this.updatePricingSummary();
    });

    const ledBarSlider = document.getElementById('light-ledbar');
    const ledBarVal = document.getElementById('light-ledbar-val');
    ledBarSlider.addEventListener('input', () => {
      const v = parseInt(ledBarSlider.value);
      ledBarVal.textContent = v;
      pergola.setLighting('ledBar', v);
      this.showLightingWarnings();
      this.updatePricingSummary();
    });

    const ledStripCb = document.getElementById('light-ledstrip');
    const ledStripOpts = document.getElementById('ledstrip-options');
    if (ledStripCb) {
      ledStripCb.addEventListener('change', () => {
        pergola.setLighting('ledStrip', ledStripCb.checked);
        if (ledStripOpts) ledStripOpts.style.display = ledStripCb.checked ? 'block' : 'none';
        // Enforce conflict: disable LED bars if strip is on
        if (ledStripCb.checked) {
          ledBarSlider.value = 0;
          ledBarVal.textContent = '0';
          pergola.setLighting('ledBar', 0);
        }
        ledBarSlider.disabled = ledStripCb.checked;
        this.showLightingWarnings();
        this.updatePricingSummary();
      });
    }

    const stripHueSlider = document.getElementById('light-strip-hue');
    const stripHueVal = document.getElementById('light-strip-hue-val');
    if (stripHueSlider) {
      stripHueSlider.addEventListener('input', () => {
        const h = parseInt(stripHueSlider.value);
        stripHueVal.style.background = `hsl(${h},90%,50%)`;
        pergola.setLighting('ledStripColor', h);
      });
    }

    const stripBrightSlider = document.getElementById('light-strip-brightness');
    const stripBrightVal = document.getElementById('light-strip-brightness-val');
    if (stripBrightSlider) {
      stripBrightSlider.addEventListener('input', () => {
        const b = parseFloat(stripBrightSlider.value);
        stripBrightVal.textContent = Math.round(b * 100) + '%';
        pergola.setLighting('ledStripBrightness', b);
      });
    }

    const solarSlider = document.getElementById('light-solar');
    const solarVal = document.getElementById('light-solar-val');
    solarSlider.addEventListener('input', () => {
      const v = parseInt(solarSlider.value);
      solarVal.textContent = v + ' /post';
      pergola.setLighting('solarPostLights', v);
      this.updatePricingSummary();
    });

    const fanSlider = document.getElementById('light-fan');
    const fanVal = document.getElementById('light-fan-val');
    fanSlider.addEventListener('input', () => {
      const v = parseInt(fanSlider.value);
      fanVal.textContent = v;
      pergola.setLighting('ceilingFan', v);

      // Auto-add a fan beam if fan requires one and none exists
      if (v > 0) {
        const hasFanBeam = pergola.beamManager.getBeams().some(b => b.type === 'accessory');
        if (!hasFanBeam) {
          pergola.addBeam('accessory', 'width', 0);
          this.renderBeamList();
        }
      }

      this.showLightingWarnings();
      this.updatePricingSummary();
    });

    const pkgSlider = document.getElementById('light-package');
    const pkgVal = document.getElementById('light-package-val');
    if (pkgSlider) {
      pkgSlider.addEventListener('input', () => {
        const v = parseInt(pkgSlider.value);
        if (pkgVal) pkgVal.textContent = v;
        pergola.setLighting('lightsAndFanPackage', v);

        // Auto-add a fan beam if package requires one and none exists
        if (v > 0) {
          const hasFanBeam = pergola.beamManager.getBeams().some(b => b.type === 'accessory');
          if (!hasFanBeam) {
            pergola.addBeam('accessory', 'width', 0);
            this.renderBeamList();
          }
        }

        this.showLightingWarnings();
        this.updatePricingSummary();
      });
    }

    // ── Info popup buttons ──
    document.querySelectorAll('.info-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.showInfoPopup(btn.dataset.info);
      });
    });
    document.getElementById('info-popup-close').addEventListener('click', () => {
      document.getElementById('info-popup').style.display = 'none';
    });
    document.getElementById('info-popup').addEventListener('click', (e) => {
      if (e.target.id === 'info-popup') e.target.style.display = 'none';
    });

    // ── Roof open/close buttons ──
    document.getElementById('btn-close-roof').addEventListener('click', () => {
      this.pergola.closeRoof();
      const slider = document.getElementById('blade-angle');
      const valEl  = document.getElementById('blade-angle-val');
      if (slider) slider.value = 0;
      if (valEl)  valEl.textContent = '0°';
    });
    document.getElementById('btn-open-roof').addEventListener('click', () => {
      this.pergola.openRoof();
      const slider = document.getElementById('blade-angle');
      const valEl  = document.getElementById('blade-angle-val');
      if (slider) slider.value = 90;
      if (valEl)  valEl.textContent = '90°';
    });

    // ── Blade angle manual slider ──
    const bladeAngleSlider = document.getElementById('blade-angle');
    const bladeAngleVal    = document.getElementById('blade-angle-val');
    if (bladeAngleSlider) {
      bladeAngleSlider.addEventListener('input', (e) => {
        const angle = parseInt(e.target.value);
        this.pergola.setBladeAngle(angle);
        if (bladeAngleVal) bladeAngleVal.textContent = `${angle}°`;
      });
    }

    // ── Architecture controls (currently hidden in redesign; no-ops) ──
    document.getElementById('arch-corbel')?.addEventListener('change', (e) => {
      pergola.setCorbelEnds(e.target.checked);
      this.updatePricingSummary();
    });
    document.getElementById('arch-cornice')?.addEventListener('change', (e) => {
      pergola.setCorniceStyle(e.target.value);
      this.updatePricingSummary();
    });
    document.getElementById('arch-cornice-led')?.addEventListener('change', (e) => {
      pergola.setCorniceLed(e.target.checked);
      this.updatePricingSummary();
    });

    // ── Weather simulation ──
    const weatherStatus = document.getElementById('weather-status');
    document.getElementById('btn-rain').addEventListener('click', () => {
      this.weather.startRain();
      weatherStatus.style.display = 'block';
      weatherStatus.textContent = 'Rain detected — roof auto-closing...';
    });
    document.getElementById('btn-snow-open').addEventListener('click', () => {
      this.weather.startSnow('open');
      this.pergola.openRoof();
      weatherStatus.style.display = 'block';
      weatherStatus.textContent = 'Snow (Option A): Louvers open — snow passes through. Resume use when cleared.';
    });
    document.getElementById('btn-snow-closed').addEventListener('click', () => {
      this.weather.startSnow('closed');
      weatherStatus.style.display = 'block';
      weatherStatus.innerHTML = 'Snow (Option B): Roof closed — snow accumulates.<br><strong style="color:#f07c7c">WARNING: Do NOT operate until fully thawed!</strong>';
    });
    document.getElementById('btn-weather-clear').addEventListener('click', () => {
      this.weather.stopWeather();
      weatherStatus.style.display = 'none';
    });

    // ── Day / Night mode ──
    document.getElementById('btn-day')?.addEventListener('click', () => {
      this.setDayMode();
    });
    document.getElementById('btn-night')?.addEventListener('click', () => {
      this.setNightMode();
    });

    // ── Scene Lighting ──
    document.getElementById('light-directional').addEventListener('input', (e) => {
      this.directionalLight.intensity = parseFloat(e.target.value);
    });
    document.getElementById('light-ambient')?.addEventListener('input', (e) => {
      this.ambientLight.intensity = parseFloat(e.target.value);
    });
    document.getElementById('light-shadow').addEventListener('change', (e) => {
      this.renderer.shadowMap.enabled = e.target.checked;
      this.directionalLight.castShadow = e.target.checked;
    });

    // ── Floor type ──
    document.getElementById('floor-type').addEventListener('change', (e) => {
      const type = e.target.value;
      this.pergola.setFloorType(type);
      this.floorMesh.material = this.pergola.getFloorMaterial();
    });

    // ── Scene ──
    document.getElementById('scene-bg')?.addEventListener('input', (e) => {
      const color = new THREE.Color(e.target.value);
      this.scene.background = color;
      if (this.scene.fog) this.scene.fog.color = color;
    });
    document.getElementById('scene-grid').addEventListener('change', (e) => {
      this.gridHelper.visible = e.target.checked;
    });
    document.getElementById('scene-axes').addEventListener('change', (e) => {
      this.axesHelper.visible = e.target.checked;
    });
    document.getElementById('btn-reset-camera').addEventListener('click', () => {
      this.resetCamera();
    });

    // ── Photo Mode ──
    document.getElementById('btn-photo-mode').addEventListener('click', () => {
      this.togglePhotoMode();
    });
    document.getElementById('hdri-select').addEventListener('change', (e) => {
      this.setEnvMapType(e.target.value);
    });
    document.getElementById('hdri-intensity').addEventListener('input', (e) => {
      this.setHDRIIntensity(parseFloat(e.target.value));
    });
    document.getElementById('hdri-background').addEventListener('change', (e) => {
      this.setHDRIBackground(e.target.checked);
    });

    // ── Color swatches ──────────────────────────────────────────────────────
    document.querySelectorAll('.swatch').forEach(swatch => {
      swatch.addEventListener('click', () => {
        const color  = swatch.dataset.color;
        const target = swatch.dataset.target; // 'frame' or 'blade'

        // Highlight selected swatch within its own row only
        swatch.closest('.preset-swatches')
          .querySelectorAll('.swatch')
          .forEach(s => s.classList.remove('active'));
        swatch.classList.add('active');

        if (color === 'wood') {
          if (target === 'blade') {
            // Apply wood grain texture to blades (calls build() internally)
            pergola.setBladeWoodGrain(true);
          }
          // Frame wood grain not supported — no-op for frame target
        } else {
          // Clear blade wood grain if a solid color is chosen for blades
          if (target === 'blade') {
            pergola.setBladeWoodGrain(false);
          }
          if (target === 'frame') {
            pergola.setColor('post', color);
            pergola.setColor('beam', color);
          } else {
            pergola.setColor('blade', color);
          }
          // setColor does not call build() — rebuild to update scene
          pergola.build();
          pergola.setBladeAngle(pergola.bladeAngle);
        }
      });
    });

    // ── Lighting package toggle ─────────────────────────────────────────────
    document.querySelectorAll('.pkg-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        document.querySelectorAll('.pkg-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        const pkg = btn.dataset.pkg;
        const customEl = document.getElementById('lighting-custom');
        if (customEl) customEl.style.display = pkg === 'custom' ? 'block' : 'none';

        if (pkg === 'package') {
          const totalBays = pergola.lightingManager.totalBays || 1;
          pergola.setLighting('lightsAndFanPackage', totalBays);
          // Package includes a fan beam — auto-add one if none exists
          const hasFanBeam = pergola.beamManager.getBeams().some(b => b.type === 'accessory');
          if (!hasFanBeam) {
            pergola.addBeam('accessory', 'width', 0);
            this.renderBeamList();
          }
          this.showLightingWarnings();
          this.updatePricingSummary();
        } else if (pkg === 'none') {
          pergola.setLighting('lightsAndFanPackage', 0);
          this.showLightingWarnings();
          this.updatePricingSummary();
        } else if (pkg === 'custom') {
          // Clear package, let individual sliders drive the model
          pergola.setLighting('lightsAndFanPackage', 0);
          this.showLightingWarnings();
          this.updatePricingSummary();
        }
      });
    });

    // ── Combined open/close slider ──────────────────────────────────────────
    const combinedSlider = document.getElementById('combined-open-slider');
    const bladeSlider    = document.getElementById('blade-angle');
    const shadeSlider    = document.getElementById('shade-open-slider');

    if (combinedSlider && bladeSlider) {
      combinedSlider.addEventListener('input', () => {
        const pct = combinedSlider.value / 100; // 0.0 → 1.0

        // Drive louvers via existing blade-angle handler
        const angle = Math.round(pct * 90);
        bladeSlider.value = angle;
        const angleVal = document.getElementById('blade-angle-val');
        if (angleVal) angleVal.textContent = angle + '°';
        bladeSlider.dispatchEvent(new Event('input'));

        // Drive shades if their row is visible
        const shadeRow = document.getElementById('shade-open-row');
        if (shadeSlider && shadeRow && shadeRow.style.display !== 'none') {
          shadeSlider.value = Math.round(pct * 100);
          shadeSlider.dispatchEvent(new Event('input'));
        }
      });
    }

    // ── Shade open/close slider (global) ───────────────────────────────────
    const shadeOpenSlider = document.getElementById('shade-open-slider');
    if (shadeOpenSlider) {
      shadeOpenSlider.addEventListener('input', () => {
        const pct = shadeOpenSlider.value / 100;
        const valEl = document.getElementById('shade-open-val');
        if (valEl) valEl.textContent = pct >= 0.5 ? 'Down' : 'Up';
        // Set position on all enabled shades
        for (const side of ['front', 'back', 'left', 'right']) {
          if (this.shadeManager.shades[side]?.enabled) {
            this.shadeManager.setShadePosition(side, pct);
            const sideSlider = document.getElementById(`shade-pos-${side}`);
            if (sideSlider) sideSlider.value = String(Math.round(pct * 100));
          }
        }
        this.pergola.build();
        this.pergola.setBladeAngle(this.pergola.bladeAngle);
      });
    }

    // ── Floating adjust panel ───────────────────────────────────────
    const floatToggle = document.getElementById('float-panel-toggle');
    if (floatToggle) {
      floatToggle.addEventListener('click', () => {
        document.getElementById('float-panel')?.classList.toggle('collapsed');
      });
    }

    const floatTimeSlider = document.getElementById('float-time-slider');
    if (floatTimeSlider) {
      floatTimeSlider.addEventListener('input', (e) => {
        // Sync to the main time slider and trigger its handler
        const mainSlider = document.getElementById('sun-time-slider');
        if (mainSlider) {
          mainSlider.value = e.target.value;
          mainSlider.dispatchEvent(new Event('input'));
        }
        // Sync the float display to match the main display
        const mainDisplay = document.getElementById('sun-time-display');
        const floatDisplay = document.getElementById('float-time-display');
        if (mainDisplay && floatDisplay) {
          floatDisplay.textContent = mainDisplay.textContent;
        }
      });
    }

    const floatFaceSun = document.getElementById('float-face-sun');
    if (floatFaceSun) {
      floatFaceSun.addEventListener('click', () => {
        document.getElementById('btn-face-sun')?.click();
      });
    }
    const floatFaceAway = document.getElementById('float-face-away');
    if (floatFaceAway) {
      floatFaceAway.addEventListener('click', () => {
        document.getElementById('btn-face-away')?.click();
      });
    }

    const floatCombined = document.getElementById('float-combined-slider');
    if (floatCombined) {
      floatCombined.addEventListener('input', (e) => {
        const mainCombined = document.getElementById('combined-open-slider');
        if (mainCombined) {
          mainCombined.value = e.target.value;
          mainCombined.dispatchEvent(new Event('input'));
        }
      });
    }

    ['clear', 'rain', 'snow'].forEach(type => {
      const floatBtn = document.getElementById(`float-btn-${type}`);
      if (floatBtn) {
        floatBtn.addEventListener('click', () => {
          const idMap = { clear: 'btn-weather-clear', rain: 'btn-rain', snow: 'btn-snow-open' };
          document.getElementById(idMap[type])?.click();
          document.querySelectorAll('.float-wx-btn').forEach(b => b.classList.remove('active'));
          floatBtn.classList.add('active');
        });
      }
    });

    // ── Split-screen toggle ────────────────────────────────────────────────
    document.getElementById('btn-split-toggle')?.addEventListener('click', () => {
      this.toggleSplitMode();
    });

    this._bindSunPanel();
  }

  toggleSplitMode() {
    this.splitMode = !this.splitMode;
    document.body.classList.toggle('split-mode', this.splitMode);
    const renderView = document.getElementById('render-view');
    if (renderView) renderView.style.display = this.splitMode ? 'block' : 'none';
    const btn = document.getElementById('btn-split-toggle');
    if (btn) btn.classList.toggle('active', this.splitMode);
    this.onResize();
  }

  // ─── Sun simulation panel bindings ───────────────────────────────────────

  _bindSunPanel() {
    const sunSlider  = document.getElementById('sun-time-slider');
    const sunDate    = document.getElementById('sun-date-input');
    const sunAnimate = document.getElementById('btn-sun-animate');
    const sunLatEl   = document.getElementById('sun-lat-input');
    const sunLngEl   = document.getElementById('sun-lng-input');

    // Sun is always-on in the redesigned form — no toggle
    this.sunSim.enabled = true;
    const dirSlider = document.getElementById('light-directional');
    if (dirSlider) dirSlider.disabled = true;

    // Set today as default date
    if (sunDate) sunDate.value = this.sunSim.getDateInputValue();

    if (sunSlider) {
      sunSlider.addEventListener('input', (e) => {
        this.sunSim.setTimeMinutes(parseInt(e.target.value, 10));
        this.updateSunLight();
        this._updateCompass();
        this.renderSunPanel();
      });
    }

    if (sunDate) {
      sunDate.addEventListener('change', (e) => {
        this.sunSim.setDateFromString(e.target.value);
        this.updateSunLight();
        this._updateCompass();
        this.renderSunPanel();
      });
    }

    if (sunAnimate) {
      sunAnimate.addEventListener('click', () => {
        this.sunSim.playing = !this.sunSim.playing;
        if (this.sunSim.playing) {
          this.sunSim.setTimeMinutes(840);  // start at 2:00 PM
          sunAnimate.textContent = 'Stop';
        } else {
          sunAnimate.textContent = 'Animate PM';
          this.updateSunLight();
          this._updateCompass();
          this.renderSunPanel();
        }
      });
    }

    // sunFacing dropdown replaced by pergolaOrientation 3D rotation (cardinal-btn / btn-face-sun)
    // sunFacing.addEventListener('change', (e) => {
    //   this.sunSim.setPergolaFacing(parseInt(e.target.value, 10));
    //   this.updateSunLight();
    //   this.renderSunPanel();
    // });

    sunLatEl.addEventListener('change', (e) => {
      const v = parseFloat(e.target.value);
      if (!isNaN(v)) this.sunSim.lat = v;
      this.updateSunLight();
      this._updateCompass();
      this.renderSunPanel();
    });

    sunLngEl.addEventListener('change', (e) => {
      const v = parseFloat(e.target.value);
      if (!isNaN(v)) this.sunSim.lng = v;
      this.updateSunLight();
      this._updateCompass();
      this.renderSunPanel();
    });

    // Quick PM presets
    const presets = [
      { id: 'btn-sun-2pm', mins: 840  },
      { id: 'btn-sun-4pm', mins: 960  },
      { id: 'btn-sun-6pm', mins: 1080 },
    ];
    for (const { id, mins } of presets) {
      document.getElementById(id)?.addEventListener('click', () => {
        this.sunSim.setTimeMinutes(mins);
        this.updateSunLight();
        this._updateCompass();
        this.renderSunPanel();
      });
    }

    // ─── Address geocoding ──────────────────────────────────────────────────
    const addressInput  = document.getElementById('sun-address-input');
    const geocodeBtn    = document.getElementById('btn-sun-geocode');
    const geocodeStatus = document.getElementById('sun-geocode-status');

    const doGeocode = async () => {
      const q = addressInput.value.trim();
      if (!q) return;
      geocodeBtn.disabled = true;
      geocodeBtn.textContent = '...';
      geocodeStatus.style.color = '#aaa';
      geocodeStatus.textContent = 'Looking up...';
      try {
        const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`;
        const res  = await fetch(url, { headers: { Accept: 'application/json' } });
        const data = await res.json();
        if (!data.length) {
          geocodeStatus.style.color = '#f07c7c';
          geocodeStatus.textContent = 'Address not found — try adding city/state';
        } else {
          const lat = parseFloat(data[0].lat);
          const lng = parseFloat(data[0].lon);
          this.sunSim.setLocation(lat, lng);
          document.getElementById('sun-lat-input').value = lat.toFixed(4);
          document.getElementById('sun-lng-input').value = lng.toFixed(4);
          geocodeStatus.style.color = '#7cb8f0';
          geocodeStatus.textContent = data[0].display_name.split(',').slice(0, 3).join(',').trim();
          this.updateSunLight();
          this._updateCompass();
          this.renderSunPanel();
        }
      } catch (_) {
        geocodeStatus.style.color = '#f07c7c';
        geocodeStatus.textContent = 'Lookup failed — check connection';
      } finally {
        geocodeBtn.disabled = false;
        geocodeBtn.textContent = 'Lookup';
      }
    };

    geocodeBtn.addEventListener('click', doGeocode);
    addressInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') doGeocode(); });

    // ── Pergola orientation controls (sun section) ─────────────────────
    const btnFaceSun  = document.getElementById('btn-face-sun');
    const btnFaceAway = document.getElementById('btn-face-away');
    if (btnFaceSun) {
      btnFaceSun.addEventListener('click', () => {
        this.setPergolaOrientation(this._getFaceSunDeg());
        document.querySelectorAll('.cardinal-btn').forEach(b => b.classList.remove('active'));
      });
    }
    if (btnFaceAway) {
      btnFaceAway.addEventListener('click', () => {
        this.setPergolaOrientation(this._getFaceAwayDeg());
        document.querySelectorAll('.cardinal-btn').forEach(b => b.classList.remove('active'));
      });
    }
    document.querySelectorAll('.cardinal-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const deg = parseFloat(btn.dataset.deg);
        this.setPergolaOrientation(deg);
        document.querySelectorAll('.cardinal-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
      });
    });

    // Flip which face (width vs length) points in the chosen direction
    document.getElementById('btn-flip-face')?.addEventListener('click', () => {
      this.facingFlipped = !this.facingFlipped;
      this.setPergolaOrientation(this.pergolaOrientation + 90);
      // Keep the same active cardinal button
    });

    // Location expand toggle
    const btnChangeLoc = document.getElementById('btn-sun-change-loc');
    if (btnChangeLoc) {
      btnChangeLoc.addEventListener('click', () => {
        const expand = document.getElementById('sun-location-expand');
        if (expand) expand.style.display = expand.style.display === 'none' ? 'block' : 'none';
      });
    }

    this._updateCompass();
    this.updateSunLight();
    this._initCompassDrag();
  }

  // ─── URL param ingestion (EasyPrice → 3D auto-config) ─────────────────────
  // Supported params:
  //   w=12          width in feet (integer or decimal, max 15)
  //   l=16          length in feet
  //   h=8           height in feet
  //   color=ecece6  hex color for all parts (no #)
  //   roof=r-blade  roof type: r-blade | r-shade | r-breeze
  //   blades=20     blade count override (6–40) — only relevant for r-blade
  //   angle=45      blade open angle (0–110)
  //   walls=front,left  comma-separated wall sides to attach
  //   led=ledbar    lighting: ledbar | ledstrip | none
  //
  // Example:
  //   https://3d.breslow.com?w=14&l=20&color=2d2d2d&roof=r-blade&angle=30&walls=front,left&led=ledbar
  readURLParams() {
    const params = new URLSearchParams(window.location.search);
    if (!params.toString()) return; // nothing to do

    const p = this.pergola;
    const IN = 1 / 0.0254; // meters → inches helper for display sync

    // ── Dimensions ──
    const w = parseFloat(params.get('w'));
    const l = parseFloat(params.get('l'));
    const h = parseFloat(params.get('h'));
    const widthIn  = !isNaN(w) ? Math.max(60, Math.min(192, w * 12)) : null;
    const lengthIn = !isNaN(l) ? Math.max(60, Math.min(276, l * 12)) : null;
    const heightIn = !isNaN(h) ? Math.max(84, Math.min(144, h * 12)) : null;

    if (widthIn || lengthIn || heightIn) {
      const curW = Math.round(p.config.width  / 0.0254);
      const curL = Math.round(p.config.length / 0.0254);
      const curH = Math.round(p.config.postHeight / 0.0254);
      p.setDimensions(
        widthIn  ?? curW,
        lengthIn ?? curL,
        heightIn ?? curH
      );
      // Sync dimension sliders
      const wSlider = document.getElementById('width-slider');
      const lSlider = document.getElementById('length-slider');
      if (wSlider && widthIn)  wSlider.value  = widthIn;
      if (lSlider && lengthIn) lSlider.value = lengthIn;
    }

    // ── Color ──
    const colorHex = params.get('color');
    if (colorHex) {
      const hex = colorHex.startsWith('#') ? colorHex : `#${colorHex}`;
      p.setColor('post',  hex);
      p.setColor('beam',  hex);
      p.setColor('blade', hex);
      // Sync color inputs
      ['color-post', 'color-beam', 'color-blade', 'color-all'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.value = hex;
      });
    }

    // ── Roof type (applies to first/only zone) ──
    const roofParam = params.get('roof');
    if (roofParam) {
      const roofMap = {
        'r-blade': ROOF_TYPES.R_BLADE,
        'r-shade': ROOF_TYPES.R_SHADE,
        'r-breeze': ROOF_TYPES.R_BREEZE,
      };
      const roofType = roofMap[roofParam.toLowerCase()];
      if (roofType) {
        const zones = p.getZones();
        if (zones.length > 0) {
          p.setZoneRoofType(zones[0].id, roofType);
        }
      }
    }

    // ── Blade angle ──
    const angle = parseFloat(params.get('angle'));
    if (!isNaN(angle)) {
      const clampedAngle = Math.max(0, Math.min(90, angle));
      p.setBladeAngle(clampedAngle);
      const slider = document.getElementById('blade-angle');
      const valEl  = document.getElementById('blade-angle-val');
      if (slider) slider.value = clampedAngle;
      if (valEl)  valEl.textContent = `${clampedAngle}°`;
    }

    // ── Walls ──
    const wallsParam = params.get('walls');
    if (wallsParam) {
      const validSides = ['front', 'back', 'left', 'right'];
      const requested = wallsParam.toLowerCase().split(',').map(s => s.trim());
      validSides.forEach(side => {
        const enabled = requested.includes(side);
        if (enabled) {
          p.setWall(side, true);
          const cb = document.getElementById(`wall-${side}`);
          if (cb) cb.checked = true;
        }
      });
    }

    // ── LED lighting ──
    const led = params.get('led');
    if (led) {
      if (led === 'ledbar') {
        p.setLighting('ledBar', 1);
        const el = document.getElementById('led-bar-count');
        if (el) el.value = 1;
      } else if (led === 'ledstrip') {
        p.setLighting('ledStrip', true);
        const el = document.getElementById('led-strip');
        if (el) el.checked = true;
      }
    }

    // ── Sun simulation: ?lat=40.83&lng=-74.24&date=2026-06-21&time=960 ────────
    const urlLat  = parseFloat(params.get('lat'));
    const urlLng  = parseFloat(params.get('lng'));
    const urlDate = params.get('date');
    const urlTime = parseFloat(params.get('time'));

    if (!isNaN(urlLat) && !isNaN(urlLng)) {
      this.sunSim.setLocation(urlLat, urlLng);
      const latEl = document.getElementById('sun-lat-input');
      const lngEl = document.getElementById('sun-lng-input');
      if (latEl) latEl.value = urlLat;
      if (lngEl) lngEl.value = urlLng;
      // Auto-enable when location is provided
      this.sunSim.enabled = true;
      const toggle = document.getElementById('sun-enable');
      if (toggle) toggle.checked = true;
      const panelBody = document.getElementById('sun-panel-body');
      if (panelBody) panelBody.style.display = 'block';
      const dirSlider = document.getElementById('light-directional');
      if (dirSlider) dirSlider.disabled = true;
    }
    if (urlDate) {
      this.sunSim.setDateFromString(urlDate);
      const dateEl = document.getElementById('sun-date-input');
      if (dateEl) dateEl.value = this.sunSim.getDateInputValue();
    }
    if (!isNaN(urlTime)) {
      this.sunSim.setTimeMinutes(urlTime);
      const sl = document.getElementById('sun-time-slider');
      if (sl) sl.value = Math.floor(urlTime);
    }
    if (this.sunSim.enabled) {
      this.updateSunLight();
      this._updateCompass();
      this.renderSunPanel();
    }

    console.log('[3D] Applied URL params:', Object.fromEntries(params));
    // EasyPrice integration: if URL params present, skip form and go straight to render
    this.showRenderView();
  }

  showFormView() {
    // Exit split mode when navigating back to form-only view
    this.splitMode = false;
    document.body.classList.remove('split-mode');
    document.getElementById('btn-split-toggle')?.classList.remove('active');

    const formView   = document.getElementById('form-view');
    const renderView = document.getElementById('render-view');
    const statusBar  = document.getElementById('status-bar');
    if (renderView) renderView.style.display = 'none';
    if (formView)   formView.style.display   = 'flex';
    if (statusBar)  statusBar.style.display  = 'none';
  }

  showRenderView() {
    const formView   = document.getElementById('form-view');
    const renderView = document.getElementById('render-view');
    const statusBar  = document.getElementById('status-bar');
    if (formView)   formView.style.display   = 'none';
    if (renderView) renderView.style.display = 'block';
    if (statusBar)  statusBar.style.display  = 'flex';
    this.onResize();
  }

  resetCamera() {
    this.camera.position.set(7, 4, 9);
    this.orbitControls.target.set(0, 1.2, 0);
    this.orbitControls.update();
    if (this.photoMode && this.photoMode.enabled) {
      this.photoMode.cameraChanged();
    }
  }

  setPergolaOrientation(degrees) {
    this.pergolaOrientation = ((degrees % 360) + 360) % 360;
    if (this.pergola && this.pergola.group) {
      this.pergola.group.rotation.y = THREE.MathUtils.degToRad(this.pergolaOrientation);
    }
    this._updateCompass();
    this._updateOrientFaceLabel();
  }

  _updateOrientFaceLabel() {
    const label = document.getElementById('orient-face-label');
    if (!label) return;
    const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const idx = Math.round(((this.pergolaOrientation % 360) + 360) % 360 / 45) % 8;
    const dirName = dirs[idx];
    // pergolaOrientation is the Y-rotation of the group.
    // At 0°, width axis (X) runs E–W; front face (–Z) points N.
    // Every 90° flip swaps which face (width or length) points in that direction.
    const flipped = this.facingFlipped || false;
    const faceLabel = flipped ? 'Length side' : 'Width side';
    label.textContent = `${faceLabel} → ${dirName}`;
  }

  _getFaceSunDeg() {
    // Rotate so the longer face (length side, ±X in local space) points toward sun.
    // azimuthDeg: 0=North, 90=East, 180=South, 270=West
    const params = this.sunSim.getLightParams();
    const az = params.azimuthDeg !== undefined
      ? params.azimuthDeg
      : (params.azimuth * 180 / Math.PI);
    return (az - 90 + 360) % 360;
  }

  _getFaceAwayDeg() {
    return (this._getFaceSunDeg() + 180) % 360;
  }

  _updateCompass() {
    if (!this.sunSim) return;
    const params = this.sunSim.getLightParams();

    // azimuthDeg is always present in getLightParams(); altitude is already in degrees.
    const azDeg  = params.azimuthDeg;
    const altDeg = params.altitude; // getLightParams() returns altitude in degrees

    // Position sun dot: compass center (55,55), ring radius 42px
    const azRad = (azDeg * Math.PI) / 180;
    const RING_R = 42;
    const cx = (55 + RING_R * Math.sin(azRad)).toFixed(1);
    const cy = (55 - RING_R * Math.cos(azRad)).toFixed(1);

    const dot  = document.getElementById('compass-sun-dot');
    const glow = document.getElementById('compass-sun-glow');
    const hit  = document.getElementById('compass-sun-hit');
    if (dot)  { dot.setAttribute('cx', cx);  dot.setAttribute('cy', cy); }
    if (glow) { glow.setAttribute('cx', cx); glow.setAttribute('cy', cy); }
    if (hit)  { hit.setAttribute('cx', cx);  hit.setAttribute('cy', cy); }

    // Visual state for manual azimuth override
    const isManual = this.sunSim.manualAzimuth !== null;
    if (dot)  dot.style.stroke      = isManual ? '#fff' : 'none';
    if (dot)  dot.style.strokeWidth = isManual ? '1.5'  : '0';
    const badge = document.getElementById('compass-manual-badge');
    if (badge) badge.style.display = isManual ? 'block' : 'none';

    // Rotate pergola rectangle in compass
    const pergola = document.getElementById('compass-pergola');
    if (pergola) {
      pergola.setAttribute('transform', `rotate(${this.pergolaOrientation}, 55, 55)`);
    }

    // Update altitude / azimuth readout
    const altEl = document.getElementById('sun-altitude-val');
    const azEl  = document.getElementById('sun-azimuth-val');
    if (altEl) altEl.textContent = altDeg.toFixed(1);
    if (azEl)  azEl.textContent  = azDeg.toFixed(1);
  }

  // ─── Interactive compass drag ─────────────────────────────────────────────
  // Sun dot drag  → sets manual azimuth override (SunCalc altitude kept)
  // Pergola rect drag → rotates 3D pergola group (same as cardinal buttons)
  _initCompassDrag() {
    const svg       = document.getElementById('compass-svg');
    const pergolaEl = document.getElementById('compass-pergola');
    if (!svg) return;

    const CX = 55, CY = 55;   // SVG viewBox center

    // Convert client coords → SVG coords
    const svgPt = (e) => {
      const pt  = svg.createSVGPoint();
      const src = e.touches ? e.touches[0] : e;
      pt.x = src.clientX;
      pt.y = src.clientY;
      return pt.matrixTransform(svg.getScreenCTM().inverse());
    };

    // Angle from SVG center, 0 = North, CW in degrees
    const angleDeg = (pt) =>
      ((Math.atan2(pt.x - CX, -(pt.y - CY)) * 180 / Math.PI) + 360) % 360;

    let dragging = null;  // 'sun' | 'pergola' | null

    const onStart = (e) => {
      const tgt = e.target;
      const isSun = tgt.id === 'compass-sun-dot' ||
                    tgt.id === 'compass-sun-glow' ||
                    tgt.id === 'compass-sun-hit';
      const isPergola = pergolaEl && pergolaEl.contains(tgt);

      if (isSun || isPergola) {
        dragging = isSun ? 'sun' : 'pergola';
        e.preventDefault();
        e.stopPropagation();
        // Disable smooth transition during drag so dot follows finger instantly
        const dot  = document.getElementById('compass-sun-dot');
        const glow = document.getElementById('compass-sun-glow');
        const hit  = document.getElementById('compass-sun-hit');
        if (dot)  dot.style.transition  = 'none';
        if (glow) glow.style.transition = 'none';
        if (hit)  hit.style.transition  = 'none';
      }
    };

    const onMove = (e) => {
      if (!dragging) return;
      e.preventDefault();
      const pt  = svgPt(e);
      const deg = angleDeg(pt);

      if (dragging === 'sun') {
        this.sunSim.manualAzimuth = deg;
      } else {
        this.setPergolaOrientation(deg);
        // Deactivate cardinal buttons since we're at a free angle
        document.querySelectorAll('.cardinal-btn').forEach(b => b.classList.remove('active'));
      }
      this.updateSunLight();
      this._updateCompass();
    };

    const onEnd = () => {
      if (!dragging) return;
      dragging = null;
      // Re-enable smooth transition after drag ends
      const dot  = document.getElementById('compass-sun-dot');
      const glow = document.getElementById('compass-sun-glow');
      const hit  = document.getElementById('compass-sun-hit');
      if (dot)  dot.style.transition  = '';
      if (glow) glow.style.transition = '';
      if (hit)  hit.style.transition  = '';
    };

    svg.addEventListener('mousedown',  onStart);
    svg.addEventListener('touchstart', onStart, { passive: false });
    window.addEventListener('mousemove',  onMove);
    window.addEventListener('touchmove',  onMove, { passive: false });
    window.addEventListener('mouseup',    onEnd);
    window.addEventListener('touchend',   onEnd);

    // Manual badge click → clear override and revert to SunCalc
    document.getElementById('compass-manual-badge')?.addEventListener('click', () => {
      this.sunSim.manualAzimuth = null;
      this.updateSunLight();
      this._updateCompass();
    });
  }

  async togglePhotoMode() {
    const btn = document.getElementById('btn-photo-mode');
    const statusEl = document.getElementById('photo-mode-status');

    if (this.photoMode.enabled) {
      this.photoMode.disable();
      // onDisable callback handles UI reset
    } else {
      btn.textContent = 'Building BVH...';
      btn.disabled = true;
      statusEl.style.display = 'block';
      document.getElementById('photo-samples').textContent = 'Building scene...';
      document.getElementById('photo-progress').style.width = '0%';

      try {
        await this.photoMode.enable();
        btn.textContent = 'Disable Photo Mode';
        btn.style.background = 'linear-gradient(135deg,#5a2a8e,#2a6a8e)';
        btn.disabled = false;
      } catch (err) {
        console.error('Photo mode failed:', err);
        btn.textContent = 'Enable Photo Mode';
        btn.style.background = 'linear-gradient(135deg,#2a1a4e,#1a3a5e)';
        btn.disabled = false;
        statusEl.style.display = 'none';
      }
    }
  }

  updatePricingSummary() {
    const info = this.pergola.getSpecInfo();

    // Update structure type displays
    document.getElementById('structure-type-val').textContent = info.structureType;
    document.getElementById('pricing-structure-type').textContent = info.structureType;
    document.getElementById('post-count-val').textContent = info.postCount;
    document.getElementById('bay-width-val').textContent = info.bayCount.widthBays;
    document.getElementById('bay-length-val').textContent = info.bayCount.lengthBays;
    document.getElementById('bay-total-val').textContent = info.bayCount.totalBays;

    // Zone-specific pricing fields
    document.getElementById('zone-count-val').textContent = info.zones.zoneCount;
    document.getElementById('corner-post-val').textContent = info.cornerPostCount;
    document.getElementById('scupper-val').textContent = info.scupperCount;

    // Show/hide multi-zone rows
    const junctionRow = document.getElementById('junction-post-row');
    const b2bRow = document.getElementById('b2b-cover-row');
    if (info.isMultiZone) {
      junctionRow.style.display = '';
      document.getElementById('junction-post-val').textContent = info.junctionPostCount;
      b2bRow.style.display = '';
      document.getElementById('b2b-cover-val').textContent = info.beamToBeamCovers;
    } else {
      junctionRow.style.display = 'none';
      b2bRow.style.display = 'none';
    }

    // Update lighting manager context
    this.pergola.lightingManager.setContext(info.bayCount.totalBays, info.postCount);

    // Run pricing calculator with full data
    this.pricing.calculate({
      widthInches: info.widthInches,
      lengthInches: info.lengthInches,
      structureType: info.structureType,
      postCount: info.postCount,
      cornerPostCount: info.cornerPostCount,
      junctionPostCount: info.junctionPostCount,
      beamCounts: info.beamCounts,
      lightingLineItems: info.lightingLineItems,
      zones: info.zones,
      scupperCount: info.scupperCount,
      beamToBeamCovers: info.beamToBeamCovers,
    });

    // Update post management panel
    document.getElementById('post-mgmt-count').textContent = info.postCount;
    const maxSpanFt = this.pergola.wallManager.hasStructuralHeader ? "30'-0\"" : "23'-0\"";
    document.getElementById('post-mgmt-maxspan').textContent = maxSpanFt;
    this.updatePostWarnings();
  }

  renderUserPostList() {
    const container = document.getElementById('user-post-list');
    const posts = this.pergola.wallManager.getUserPosts();
    container.innerHTML = '';

    for (const post of posts) {
      const entry = document.createElement('div');
      entry.className = 'item-list-entry';

      const label = document.createElement('span');
      label.className = 'entry-label';
      label.textContent = `${post.label} (${post.x.toFixed(0)}", ${post.z.toFixed(0)}")`;

      const removeBtn = document.createElement('button');
      removeBtn.className = 'btn-remove';
      removeBtn.textContent = 'x';
      removeBtn.addEventListener('click', () => {
        this.pergola.removeUserPost(post.id);
        this.renderUserPostList();
        this.updatePricingSummary();
      });

      entry.appendChild(label);
      entry.appendChild(removeBtn);
      container.appendChild(entry);
    }

    // Update post count display
    const info = this.pergola.getSpecInfo();
    document.getElementById('post-mgmt-count').textContent = info.postCount;
    const maxSpanFt = this.pergola.wallManager.hasStructuralHeader ? "30'-0\"" : "23'-0\"";
    document.getElementById('post-mgmt-maxspan').textContent = maxSpanFt;
  }

  renderShadeList() {
    // Buttons are static HTML — just sync active/disabled states and deploy sliders
    const walls  = this.pergola.config.walls;
    const shades = this.shadeManager.shades;

    for (const side of ['front', 'back', 'left', 'right']) {
      const hasWall = !!walls[side];
      const shade   = shades[side];
      const isOn    = shade.enabled && !hasWall;

      const btn = document.getElementById(`shade-btn-${side}`);
      if (btn) {
        btn.classList.toggle('active', isOn);
        btn.classList.toggle('disabled', hasWall);
        btn.disabled = hasWall;
        btn.title = hasWall ? 'Wall attached — cannot add shade' : '';
      }

      const deployRow = document.getElementById(`shade-deploy-${side}`);
      if (deployRow) deployRow.style.display = isOn ? 'flex' : 'none';

      const posSlider = document.getElementById(`shade-pos-${side}`);
      if (posSlider) posSlider.value = String(Math.round(shade.position * 100));

      const posVal = document.getElementById(`shade-pos-${side}-val`);
      if (posVal) posVal.textContent = Math.round(shade.position * 100) + '%';
    }
    this._updateShadeOpenRow();
  }

  _updateShadeOpenRow() {
    const row = document.getElementById('shade-open-row');
    if (!row) return;
    const anyEnabled = ['front', 'back', 'left', 'right']
      .some(s => this.shadeManager.shades[s]?.enabled);
    row.style.display = anyEnabled ? 'flex' : 'none';
  }

  updatePostWarnings() {
    const warnings = this.pergola.getPostWarnings();
    const box = document.getElementById('post-warnings');

    if (warnings.length === 0) {
      box.style.display = 'none';
      return;
    }

    box.style.display = 'block';
    box.className = 'warning-box';
    box.innerHTML = warnings.map(w => `<div>${w}</div>`).join('');
  }

  renderBeamList() {
    const container = document.getElementById('beam-list');
    const beams = this.pergola.beamManager.getBeams();
    container.innerHTML = '';

    for (const beam of beams) {
      const spec = BEAM_TYPES[beam.type];
      const entry = document.createElement('div');
      entry.className = 'item-list-entry';

      const label = document.createElement('span');
      label.className = 'entry-label';
      label.textContent = spec.shortLabel;
      label.title = spec.description;

      const snapBtn = document.createElement('button');
      snapBtn.className = 'btn-snap';
      snapBtn.textContent = 'Center';
      snapBtn.title = 'Snap to center';
      snapBtn.addEventListener('click', () => {
        this.pergola.snapBeamToCenter(beam.id);
        slider.value = 0;
        this.updatePricingSummary();
      });

      const slider = document.createElement('input');
      slider.type = 'range';
      const widthIn = this.pergola.config.width / IN_TO_M;
      const lengthIn = this.pergola.config.length / IN_TO_M;
      const maxOffset = (beam.axis === 'width' ? lengthIn : widthIn) / 2;
      slider.min = -maxOffset;
      slider.max = maxOffset;
      slider.step = 1;
      slider.value = beam.positionIn;
      slider.addEventListener('input', () => {
        const result = this.pergola.moveBeam(beam.id, parseFloat(slider.value));
        if (result && result.snapped) {
          document.getElementById('beam-snap-label').textContent = `Snapped: ${result.label}`;
          slider.value = result.positionIn;
        } else {
          document.getElementById('beam-snap-label').textContent = '';
        }
        this.updatePricingSummary();
      });

      const removeBtn = document.createElement('button');
      removeBtn.className = 'btn-remove';
      removeBtn.textContent = 'x';
      removeBtn.addEventListener('click', () => {
        this.pergola.removeBeam(beam.id);
        this.renderBeamList();
        this.updatePricingSummary();
      });

      entry.appendChild(label);
      entry.appendChild(snapBtn);
      entry.appendChild(slider);
      entry.appendChild(removeBtn);
      container.appendChild(entry);
    }
  }

  renderZoneList() {
    const container = document.getElementById('zone-list');
    const zones = this.pergola.getZones();
    const zm = this.pergola.getZoneManager();
    const isWidthAxis = zm.isWidthAxis();
    while (container.firstChild) container.removeChild(container.firstChild);

    // Show layout direction banner when multiple zones exist
    if (zones.length > 1) {
      const banner = document.createElement('div');
      banner.className = 'zone-axis-banner';
      banner.textContent = isWidthAxis
        ? 'Zones stacked along Width — each zone adds width'
        : 'Zones stacked along Length — each zone adds depth';
      container.appendChild(banner);
    }

    for (const zone of zones) {
      const entry = document.createElement('div');
      entry.className = 'zone-entry';

      // Header row: zone label + roof type selector + remove button
      const header = document.createElement('div');
      header.className = 'zone-entry-header';

      const label = document.createElement('span');
      label.className = 'zone-label';
      label.textContent = `Zone ${zones.indexOf(zone) + 1}`;

      const select = document.createElement('select');
      select.className = 'zone-roof-select';
      for (const [key, info] of Object.entries(ROOF_TYPES)) {
        const opt = document.createElement('option');
        opt.value = key;
        opt.textContent = info.label;
        if (key === zone.roofType) opt.selected = true;
        select.appendChild(opt);
      }
      select.addEventListener('change', () => {
        this.pergola.updateZone(zone.id, { roofType: select.value });
        this.renderZoneList();
        this.updatePricingSummary();
      });

      const removeBtn = document.createElement('button');
      removeBtn.className = 'zone-remove-btn';
      removeBtn.textContent = 'x';
      removeBtn.disabled = zones.length <= 1;
      removeBtn.addEventListener('click', () => {
        this.pergola.removeZone(zone.id);
        this.renderZoneList();
        this.updatePricingSummary();
      });

      header.appendChild(label);
      header.appendChild(select);
      header.appendChild(removeBtn);
      entry.appendChild(header);

      // Frame Dimensions sub-header
      const dimHeader = document.createElement('h3');
      dimHeader.style.cssText = 'margin-top:6px;font-size:9px';
      dimHeader.textContent = 'Frame Dimensions';
      entry.appendChild(dimHeader);

      // Width slider
      const widthRow = document.createElement('div');
      widthRow.className = 'ctrl-row';
      const wLabel = document.createElement('span');
      wLabel.className = 'ctrl-label';
      wLabel.textContent = 'Width';
      const wSlider = document.createElement('input');
      wSlider.type = 'range';
      wSlider.min = 72;
      wSlider.max = 192;
      wSlider.step = 6;
      wSlider.value = zone.widthInches;
      wSlider.className = 'ctrl-slider zone-w-slider';
      const wVal = document.createElement('span');
      wVal.className = 'ctrl-val';
      wVal.textContent = this.inchesToDisplay(zone.widthInches);
      wSlider.addEventListener('input', () => {
        const v = parseFloat(wSlider.value);
        wVal.textContent = this.inchesToDisplay(v);
        if (zones.length === 1) {
          const curL = parseFloat(lSlider.value);
          const hIn = Math.round(this.pergola.config.postHeight / IN_TO_M);
          this.pergola.setDimensions(v, curL, hIn);
        } else if (!this.pergola.zoneManager.isWidthAxis()) {
          // linear_length: width is shared — update all zones together
          this.pergola.setSharedWidth(v);
          // Sync all other width sliders in the list
          container.querySelectorAll('.zone-w-slider').forEach(s => {
            s.value = v;
            const valEl = s.nextElementSibling;
            if (valEl) valEl.textContent = this.inchesToDisplay(v);
          });
        } else {
          // linear_width: each zone has its own width
          this.pergola.updateZone(zone.id, { widthInches: v });
        }
        this.updateOverallDims();
        this.updatePricingSummary();
      });
      widthRow.appendChild(wLabel);
      widthRow.appendChild(wSlider);
      widthRow.appendChild(wVal);
      entry.appendChild(widthRow);

      // Length slider
      const lengthRow = document.createElement('div');
      lengthRow.className = 'ctrl-row';
      const lLabel = document.createElement('span');
      lLabel.className = 'ctrl-label';
      lLabel.textContent = 'Length';
      const lSlider = document.createElement('input');
      lSlider.type = 'range';
      lSlider.min = 72;
      lSlider.max = 276;
      lSlider.step = 6;
      lSlider.value = zone.lengthInches;
      lSlider.className = 'ctrl-slider zone-l-slider';
      const lVal = document.createElement('span');
      lVal.className = 'ctrl-val';
      lVal.textContent = this.inchesToDisplay(zone.lengthInches);
      lSlider.addEventListener('input', () => {
        const v = parseFloat(lSlider.value);
        lVal.textContent = this.inchesToDisplay(v);
        if (zones.length === 1) {
          const curW = parseFloat(wSlider.value);
          const hIn = Math.round(this.pergola.config.postHeight / IN_TO_M);
          this.pergola.setDimensions(curW, v, hIn);
        } else if (this.pergola.zoneManager.isWidthAxis()) {
          // linear_width: length is shared — update all zones together
          this.pergola.setSharedLength(v);
          container.querySelectorAll('.zone-l-slider').forEach(s => {
            s.value = v;
            const valEl = s.nextElementSibling;
            if (valEl) valEl.textContent = this.inchesToDisplay(v);
          });
        } else {
          // linear_length: each zone has its own length
          this.pergola.updateZone(zone.id, { lengthInches: v });
        }
        this.updateOverallDims();
        this.updatePricingSummary();
      });
      lengthRow.appendChild(lLabel);
      lengthRow.appendChild(lSlider);
      lengthRow.appendChild(lVal);
      entry.appendChild(lengthRow);

      // Post Height slider (global — changing any zone's height changes all)
      const heightRow = document.createElement('div');
      heightRow.className = 'ctrl-row';
      const hLabel = document.createElement('span');
      hLabel.className = 'ctrl-label';
      hLabel.textContent = 'Height';
      const hSlider = document.createElement('input');
      hSlider.type = 'range';
      hSlider.min = 84;
      hSlider.max = 144;
      hSlider.step = 6;
      hSlider.value = Math.round(this.pergola.config.postHeight / IN_TO_M);
      hSlider.className = 'ctrl-slider';
      const hVal = document.createElement('span');
      hVal.className = 'ctrl-val';
      hVal.textContent = this.inchesToDisplay(Math.round(this.pergola.config.postHeight / IN_TO_M));
      hSlider.addEventListener('input', () => {
        const v = parseFloat(hSlider.value);
        hVal.textContent = this.inchesToDisplay(v);
        this.pergola.setPostHeight(v);
        // Update all height sliders to match
        container.querySelectorAll('[data-height-slider]').forEach(s => {
          s.value = v;
          s.nextSibling.textContent = this.inchesToDisplay(v);
        });
        this.updatePricingSummary();
      });
      hSlider.setAttribute('data-height-slider', 'true');
      heightRow.appendChild(hLabel);
      heightRow.appendChild(hSlider);
      heightRow.appendChild(hVal);
      entry.appendChild(heightRow);

      // Spec readout per zone
      const specDiv = document.createElement('div');
      specDiv.className = 'spec-readout';
      specDiv.innerHTML = 'Post: 6&frac12;" &times; 6&frac12;" &bull; Beam: 14"<br>Max: 16\'-0" W &times; 23\'-0" L';
      entry.appendChild(specDiv);

      container.appendChild(entry);
    }

    // Show zone validation errors
    const zoneErrors = this.pergola.getZoneManager().getValidationErrors();
    if (zoneErrors.length > 0) {
      const errDiv = document.createElement('div');
      errDiv.className = 'warning-box error';
      errDiv.style.marginTop = '6px';
      for (const e of zoneErrors) {
        const d = document.createElement('div');
        d.textContent = e;
        errDiv.appendChild(d);
      }
      container.appendChild(errDiv);
    }

    this.updateOverallDims();
  }

  updateOverallDims() {
    const info = this.pergola.getSpecInfo();
    const wDisplay = this.inchesToDisplay(info.widthInches);
    const lDisplay = this.inchesToDisplay(info.lengthInches);
    document.getElementById('overall-dims-val').innerHTML = `${wDisplay} &times; ${lDisplay}`;
  }

  showZoneValidationError(msg) {
    // Show zone errors in the zone list container, not in the lighting warnings
    const container = document.getElementById('zone-list');
    const errDiv = document.createElement('div');
    errDiv.className = 'warning-box error';
    errDiv.style.marginTop = '6px';
    errDiv.textContent = msg;
    container.appendChild(errDiv);
    setTimeout(() => { errDiv.remove(); }, 3000);
  }

  showLightingWarnings() {
    const result = this.pergola.getLightingWarnings();
    const box = document.getElementById('lighting-warnings');
    const messages = [...result.errors, ...result.warnings];

    if (messages.length === 0) {
      box.style.display = 'none';
      return;
    }

    box.style.display = 'block';
    box.className = 'warning-box' + (result.errors.length > 0 ? ' error' : '');
    box.innerHTML = messages.map(m => `<div>${m}</div>`).join('');
  }

  showInfoPopup(type) {
    const popup = document.getElementById('info-popup');
    const title = document.getElementById('info-popup-title');
    const body = document.getElementById('info-popup-body');

    if (type === 'zones') {
      title.textContent = 'Multi-Zone System';
      body.innerHTML = `
        <div class="info-item"><strong>What are Zones?</strong><br>
          Each zone is a bay with its own roof type, length, and height. Zones are placed side by side along the length axis.</div>
        <div class="info-item"><strong>Mixed Roof Types</strong><br>
          Unlike Azenco's configurator (which only supports one roof type per config), our system lets you mix:
          <div style="margin:4px 0"><span class="zone-roof-badge r-blade">R-Blade</span> Adjustable aluminum louver blades</div>
          <div style="margin:4px 0"><span class="zone-roof-badge r-shade">R-Shade</span> Retractable fabric shade</div>
          <div style="margin:4px 0"><span class="zone-roof-badge r-breeze">R-Breeze</span> Fixed screen panels</div>
        </div>
        <div class="info-item"><strong>Junction Posts</strong><br>
          Posts shared between adjacent zones at zone boundaries. Automatically calculated.</div>
        <div class="info-item"><strong>Scuppers</strong><br>
          Drainage elements at each zone's gutter terminations (2 per zone).</div>
        <div class="info-item"><strong>Beam-to-Beam Covers</strong><br>
          Connectors between adjacent zone frames (1 per junction).</div>
      `;
    } else if (type === 'beams') {
      title.textContent = 'Beam Types';
      body.innerHTML = `
        <div class="info-item"><strong>Accessory Beam (Fan Beam)</strong><br>
          Non-structural beam for mounting ceiling fans or accessories. 4" x 4" profile. Required for each ceiling fan.</div>
        <div class="info-item"><strong>Structural Beam (Header Beam)</strong><br>
          Primary load-bearing member for spanning large openings or where posts are removed. Transfers roof loads to supporting posts.</div>
        <div class="info-item"><strong>Integrated Heater Beam</strong><br>
          Structural beam with built-in vents for one Infratech recessed heater. Required per heater.</div>
        <div class="info-item"><strong>Reinforcement (Pass-Through Gutter)</strong><br>
          Strengthens connections between adjacent bays. Required when joining bays end-to-end or creating passthrough openings.</div>
      `;
    } else if (type === 'lighting') {
      title.textContent = 'Lighting & Fan Options';
      body.innerHTML = `
        <div class="info-item"><strong>Recessed Lights</strong><br>
          Max 2 per bay, flank the ceiling fan in center position.</div>
        <div class="info-item"><strong>LED Bar / Ramp Lights</strong><br>
          4 per bay (1 per beam side). Cannot combine with LED Strip lights.</div>
        <div class="info-item"><strong>LED Strip Lights</strong><br>
          Color-changing perimeter ambient light on louver undersides. Cannot combine with LED bars. Requires Control Box/Driver (auto-added).</div>
        <div class="info-item"><strong>Solar Post Lights</strong><br>
          Up to 4 per post, illuminate downward. Solar powered, no wiring needed.</div>
        <div class="info-item"><strong>Ceiling Fan</strong><br>
          1 per bay, requires a Fan Beam (accessory beam) for mounting.</div>
        <div class="info-item"><strong>Lights &amp; Fan Package</strong><br>
          Bundle: 2 Recessed + 4 LED Bars + 1 Ceiling Fan + 1 Fan Beam per bay.</div>
        <div class="info-item"><strong>Bond Bridge Pro + Bond Breeze Multi Sensor</strong><br>
          Smart home automation. Bond Bridge Pro enables app/voice control. Bond Breeze Multi Sensor for weather-responsive louver automation.</div>
      `;
    }

    if (type === 'shades') {
      title.textContent = 'Magnatrack Retractable Shades';
      const items = [
        ['Progressive Screens Magnatrack', 'Motorized solar mesh roller shade. 5.75\u2033 roller in 7.5\u2033 \u00d7 7.5\u2033 housing. Max drop 16\u2019. Solar mesh reduces glare and heat while preserving the view.'],
        ['Inside Mount (IUH)', 'Housing sits under the fascia beam on the inner face. Tracks run down the inside of the posts. Cleanest look from outside \u2014 hardware is concealed within the structure.'],
        ['Outside Mount (OAH)', 'Housing mounts on the outer face of the fascia at the very top. Tracks overlap the post outer face 3\u2033 inboard. Shade covers the full height from fascia top to ground.'],
        ['Solar Mesh Screen', 'Black semi-transparent mesh (~65% opacity). Blocks sun glare and radiant heat while maintaining visibility through to the outside. Motorized with wireless remote.'],
        ['Wall Conflict', 'Sides with a wall attached cannot have shades. Attaching a wall to a side will automatically disable the shade for that side.'],
      ];
      const frag = document.createDocumentFragment();
      items.forEach(([heading, text], i) => {
        const div = document.createElement('div');
        div.className = 'info-item';
        if (i === items.length - 1) div.style.border = 'none';
        const strong = document.createElement('strong');
        strong.textContent = heading;
        const br = document.createElement('br');
        const txt = document.createTextNode(text);
        div.appendChild(strong);
        div.appendChild(br);
        div.appendChild(txt);
        frag.appendChild(div);
      });
      while (body.firstChild) body.removeChild(body.firstChild);
      body.appendChild(frag);
    }

    if (type === 'architecture') {
      title.textContent = 'Architecture Options';
      body.innerHTML = `
        <div class="info-item"><strong>Corbel Ends</strong><br>
          Decorative triangular brackets at post-to-beam junctions. Adds a classic architectural detail.</div>
        <div class="info-item"><strong>Cornice (1 Step)</strong><br>
          A single decorative molding profile running around the full perimeter on top of the fascia. Adds depth and shadow lines.</div>
        <div class="info-item"><strong>Cornice (2 Steps)</strong><br>
          A double-stepped cornice for a more pronounced architectural profile. More ornate crown-like appearance.</div>
        <div class="info-item"><strong>Cornice RGBW LED Strip</strong><br>
          LED strip integrated into the cornice, facing downward for ambient down-facing light. Color-changing capability.</div>
        <div class="info-item" style="border:none"><strong>Inside Corner Cover</strong><br>
          Mandatory hardware that hides screws at internal corners. Not visible from outside and not shown in 3D view. Auto-included in pricing.</div>
      `;
    }

    if (type === 'weather') {
      title.textContent = 'Wind & Snow Load Ratings';
      body.innerHTML = `
        <div class="info-item"><strong>Wind Load Ratings</strong><br>
          <p>Louver position affects wind resistance:</p>
          <div style="margin:4px 0"><strong style="color:#7cf0a8">Closed (0°):</strong> 110 mph — maximum resistance</div>
          <div style="margin:4px 0"><strong style="color:#f0d07c">Partial (45°):</strong> 75 mph — moderate sail effect</div>
          <div style="margin:4px 0"><strong style="color:#f07c7c">Open (170°):</strong> 50 mph — highest wind catch</div>
          <p style="margin-top:6px;font-size:10px;color:#888">Best practice: Auto-close with Bond Breeze Multi Sensor in high winds.</p>
        </div>
        <div class="info-item"><strong>Snow Load Equivalents</strong><br>
          <div style="margin:4px 0">1" fresh snow = ~1 lb/sq ft <span style="color:#888">(thin cotton blanket)</span></div>
          <div style="margin:4px 0">6" fresh snow = ~3 lb/sq ft <span style="color:#888">(bag of flour per 4 sq ft)</span></div>
          <div style="margin:4px 0">12" packed snow = ~15 lb/sq ft <span style="color:#888">(small child on every 4x4 area)</span></div>
          <div style="margin:4px 0">6" ice = ~30 lb/sq ft <span style="color:#888">(large dog on every 4x4 area)</span></div>
        </div>
        <div class="info-item"><strong>Snow Options (NE Weather)</strong><br>
          <div style="margin:4px 0"><strong style="color:#7cf0a8">Option A — Leave Open:</strong> Snow passes through. Use again immediately after snow clears.</div>
          <div style="margin:4px 0"><strong style="color:#f07c7c">Option B — Leave Closed:</strong> Snow accumulates. MUST wait for full thaw to avoid motor damage.</div>
        </div>
      `;
    }

    if (type === 'posts') {
      title.textContent = 'Post Placement Rules';
      body.innerHTML = `
        <div class="info-item"><strong>Minimum Posts</strong><br>
          The system always provides the minimum required posts. Corner posts are placed at each open corner. Intermediate posts are auto-added when a span exceeds the maximum.</div>
        <div class="info-item"><strong>Standard Span: 23'</strong><br>
          Maximum unsupported span on any edge is 23'. If your pergola exceeds 23' on any side, intermediate posts are added automatically.</div>
        <div class="info-item"><strong>Extended Span: 30' (with Structural Header)</strong><br>
          Adding a Structural Header Beam extends the max span to 30'. Only 2 posts per span are needed. Beyond 30' is not supported.</div>
        <div class="info-item"><strong>Cantilever</strong><br>
          Any corner post can be inset up to 3' (36") from the corner, creating a cantilevered overhang. The beam extends past the post to the original corner position.</div>
        <div class="info-item"><strong>Custom Posts</strong><br>
          You can add extra posts (e.g., a center post between zones) for aesthetics or additional support. These are optional and can be removed at any time.</div>
        <div class="info-item" style="border:none"><strong>Removing Posts</strong><br>
          Only intermediate and custom posts can be removed. Corner and junction posts are structurally required. Removing a post is only allowed if the resulting span stays within limits.</div>
      `;
    }

    popup.style.display = 'flex';
  }

  // ─── Sun simulation ───────────────────────────────────────────────────────

  updateSunLight() {
    if (!this.sunSim.enabled) {
      this.directionalLight.position.set(
        this._defaultLightPos.x, this._defaultLightPos.y, this._defaultLightPos.z);
      this.directionalLight.color.setHex(this._defaultLightColor);
      if (this.scene.backgroundRotation !== undefined)
        this.scene.backgroundRotation.set(0, 0, 0);
      if (this.scene.environmentRotation !== undefined)
        this.scene.environmentRotation.set(0, 0, 0);
      return;
    }
    const p = this.sunSim.getLightParams();
    this.directionalLight.position.set(p.x, p.y, p.z);
    this.directionalLight.color.setHex(p.color);
    this.directionalLight.intensity = p.isNight ? 0.0 : p.intensity;
    this.directionalLight.shadow.camera.updateProjectionMatrix();
    // Rotate HDRI sky so its baked sun aligns with the calculated light direction.
    // HDRI native sun sits at ~south (π rad) when rotY=0; rotY shifts it to match azRad.
    const azRad = p.azimuthDeg * (Math.PI / 180);
    const rotY  = Math.PI - azRad;
    if (this.scene.backgroundRotation !== undefined)
      this.scene.backgroundRotation.set(0, rotY, 0);
    if (this.scene.environmentRotation !== undefined)
      this.scene.environmentRotation.set(0, rotY, 0);
  }

  renderSunPanel() {
    const timeEl  = document.getElementById('sun-time-display');
    const altEl   = document.getElementById('sun-altitude-val');
    const azEl    = document.getElementById('sun-azimuth-val');
    const slider  = document.getElementById('sun-time-slider');
    if (!timeEl) return;
    const p = this.sunSim.getLightParams();
    timeEl.textContent = this.sunSim.getTimeDisplay();
    altEl.textContent  = `${p.altitude.toFixed(1)}°`;
    azEl.textContent   = `${p.azimuthDeg.toFixed(0)}°`;
    if (slider && document.activeElement !== slider)
      slider.value = Math.floor(this.sunSim.timeMinutes);
  }

  setDayMode() {
    if (this.sunSim && this.sunSim.enabled) return;
    this.isNightMode = false;
    const skyColor = new THREE.Color(0xd4e6f1);
    this.scene.background = skyColor;
    if (this.scene.fog) this.scene.fog.color = skyColor;
    // If HDRI is loaded, let it carry the lighting
    if (this.hdriEnvMap) {
      this.directionalLight.intensity = 0.5;
      this.ambientLight.intensity = 0.1;
      this.hemiLight.intensity = 0.0;
      this.renderer.toneMappingExposure = 0.85;
      this.scene.environmentIntensity = 1.2;
    } else {
      this.directionalLight.intensity = 1.0;
      this.ambientLight.intensity = 0.3;
      this.hemiLight.intensity = 0.2;
      this.renderer.toneMappingExposure = 0.9;
    }
    this.bloomEnabled = false;
    if (this.bloomPass) this.bloomPass.enabled = false;
    document.getElementById('scene-bg').value = '#d4e6f1';
  }

  setNightMode() {
    if (this.sunSim && this.sunSim.enabled) return;
    this.isNightMode = true;
    const nightColor = new THREE.Color(0x080812);
    this.scene.background = nightColor;
    if (this.scene.fog) this.scene.fog.color = nightColor;
    this.directionalLight.intensity = 0.04;
    this.ambientLight.intensity = 0.06;
    this.hemiLight.intensity = 0.04;
    this.renderer.toneMappingExposure = 0.8;
    // Enable bloom at night for LED glow
    this.bloomEnabled = true;
    if (this.bloomPass) {
      this.bloomPass.enabled = true;
      this.bloomPass.strength = 0.8;
      this.bloomPass.threshold = 0.4;
    }
    document.getElementById('scene-bg').value = '#080812';
  }

  enforceMaxWalls() {
    const wallSides = ['front', 'back', 'left', 'right'];
    const checkedCount = wallSides.filter(s =>
      document.getElementById(`wall-${s}`).checked
    ).length;
    for (const side of wallSides) {
      const el = document.getElementById(`wall-${side}`);
      if (!el.checked) {
        el.disabled = checkedCount >= 3;
      }
    }
  }

  onResize() {
    const renderView = document.getElementById('render-view');
    const w = (renderView && renderView.clientWidth)  ? renderView.clientWidth  : window.innerWidth;
    const h = (renderView && renderView.clientHeight) ? renderView.clientHeight : window.innerHeight;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
    if (this.composer) this.composer.setSize(w, h);
    if (this.ssaoPass) this.ssaoPass.setSize(w, h);
    if (this.photoMode && this.photoMode.enabled) {
      this.photoMode.cameraChanged();
    }
  }

  animate() {
    requestAnimationFrame(() => this.animate());
    const renderView = document.getElementById('render-view');
    if (renderView && renderView.style.display === 'none') return;
    if (this.stats) this.stats.begin();

    this.weather.update();
    this.orbitControls.update();

    // Sun simulation advance (Animate PM mode: 2 PM → 7 PM in ~8 seconds)
    if (this.sunSim && this.sunSim.enabled && this.sunSim.playing) {
      this.sunSim.advanceTimeMinutes(0.625);
      if (this.sunSim.timeMinutes >= 1140) {  // stop at 7 PM
        this.sunSim.timeMinutes = 1140;
        this.sunSim.playing = false;
        const btn = document.getElementById('btn-sun-animate');
        if (btn) btn.textContent = 'Animate PM';
      }
      this.updateSunLight();
      this._updateCompass();
      this.renderSunPanel();
    }

    // Photo mode: path tracer handles its own rendering
    if (this.photoMode && this.photoMode.enabled) {
      this.photoMode.render();
    } else if (this.ssaoEnabled || (this.bloomEnabled && this.composer)) {
      // Use composer for SSAO and/or bloom
      this.composer.render();
    } else {
      this.renderer.render(this.scene, this.camera);
    }

    if (this.stats) this.stats.end();

    // Status bar info (throttled to 1Hz)
    const elapsed = this.clock.getElapsedTime();
    this.frameCount++;
    if (elapsed - this.lastFpsTime >= 1) {
      const fps = Math.round(this.frameCount / (elapsed - this.lastFpsTime));
      document.getElementById('fps-counter').textContent = `FPS: ${fps}`;
      this.frameCount = 0;
      this.lastFpsTime = elapsed;
    }

    const info = this.renderer.info;
    document.getElementById('render-info').textContent =
      `Tris: ${info.render.triangles} | Calls: ${info.render.calls} | MB: ${(info.memory.geometries * 0.001).toFixed(1)}`;
  }
}

new App();
